import { extendedColord as colord } from './lib/extendedColord'

import { PRIMARY_COLOR, PRIMARY_HOVER_COLOR } from './constants/colors'
import { useTheme } from 'styled-components'

const mainColor = colord(PRIMARY_COLOR)

export const colors = {
  azure: '#2EA1F7',
  comment: '#E07B1D',
  search: '#E07B1D',
  primary: mainColor.toHex(),
  secondary: '#0A4FBD',
  tooltipBackground: '#353c48',
  silver: '#C7D0D7',
  silverLight: '#F7F9FB',
  'silverLight:emphasis': '#e7ebf7',
  silverMedium: '#BDCCD7',
  grey: '#9A9FA8',
  greyLight: '#628198',
  greyMedium: '#A4BFD2',
  greyDark: '#385B75',
  black: '#16364D',
  blue: '#0063F8',
  blueLigth: '#1E95EE',
  darkBlue: '#0A4FBD',
  green: '#21C784',
  darkGreen: '#149D66',
  red: '#E01D5F',
  redLight: '#F85359',
  delete: '#E01D5F',
  danger: '#E01D5F',
  mediumRed: '#FFE2EC',
  darkRed: '#AF1A4C',
  white: '#FFFFFF',
  orange: '#E0631D',
  warn: '#E0631D',
  orangeLight: '#FDD0B6',
  yellow: '#fcd500'
} as const

export const defaultTheme = () => {
  return {
    colors,
    mainColor: colors.primary,
    lockBanner: {
      bannerBackground: '#E07B1D',
      bannerBackgroundButton: colors.primary,
      bannerBackgroundButtonHover: PRIMARY_HOVER_COLOR
    },
    formColors: {
      black: '#333'
    },
    commentColors: {
      counter: '#EF7F31',
      footer: { title: '#7F8FA4' }
    },
    topHeader: {
      height: '56px',
      borderColor: '#DFE2E5',
      tabTextColor: '#354052'
    },
    item: {
      background: '#EFF3F8',
      dialogButton: '#eee',
      icon: '#8a8a8a',
      iconBackground: '#dadada',
      iconHoverBackground: '#c4c4c4',
      hover: colors['silverLight:emphasis'],
      focus: 'white'
    },
    carousel: {
      background: colors.silverLight,
      itemBackground: '#EFF3F8',
      image: '#797979',
      imageBackground: '#DAE0E5',
      title: colors.black,
      subtitle: '#aaaaaa',
      text: colors.black,
      button: colors.primary,
      hoverButton: PRIMARY_HOVER_COLOR,
      hoverButtonText: PRIMARY_HOVER_COLOR,
      buttonBackground: '#e7e7e7',
      addButtom: '#cecece',
      addButtomHover: '#797979'
    },
    checkbox: {
      sizes: {
        sm: '13px',
        md: '16px',
        lg: '20px'
      },
      background: '1991EB'
    },
    dialog: {
      background: '#fff',
      border: '#DAE0E5',
      title: '#b3b3b3',
      usages: '#cb8843'
    },
    buttons: {
      // Tadyto se snad casem prjmenuje/abstrahuje do nejakeho obecnejsi prvku nez je dropdown button
      dropdown: {
        background: 'linear-gradient(0deg, #f2f4f7 0%, #ffffff 100%)',
        borderRadius: '4px'
      }
    },
    dropdown: {
      border: '1.5px solid #CED4D9',
      boxShadow: '0px 7px 31px 0px #0000001A',
      borderRadius: '10px',
      padding: '11px 8px 11px 8px',
      minWidth: '50px',
      item: {
        fontSize: '14px',
        padding: '9px 9px 7px',
        activeBgColor: '#F2F2F2',
        iconMargin: '0 5px 0 0',
        description: {
          color: '#9A9FA8',
          fontSize: '10px',
          margin: '0 0 2px 4px'
        }
      },
      itemGroup: {
        fontSize: '15px',
        margin: '12px 9px 7px',
        color: '#9a9fa8'
      }
    },
    stepTypeSelector: {
      background: colors.silverLight,
      icon: '#eee',
      iconBackground: colors.primary,
      iconHover: PRIMARY_HOVER_COLOR,
      closeIcon: '#8c8c8c',
      closeIconHover: colors.primary
    },
    step: {
      color: colors.black,
      expandableColor: '#eee',
      background: '#EFF3F8',
      expandableBackground: colors.primary,
      expandableHover: mainColor.alpha(0.7).toHex(),
      wrapperBackground: colors.silverLight,
      button: '#8a8a8a',
      buttonBackground: '#dadada',
      buttonHoverBackground: '#c3c3c3',
      icon: '#eee',
      iconBackground: colors.primary,
      expandIconBackground: colors.primary,
      expandIconHoverBackground: mainColor.alpha(0.7).toHex(),
      importantColor: '#b51e19',
      conditionedBackground: '#E07B1D',
      commentedHighlight: `box-shadow: 0px 0px 0px 1px ${colors.primary};`,
      hover: '#e7ebf7',
      focus: 'white'
    },
    arrow: {
      size: 25,
      color: '#e6e6e6',
      hoverColor: colors.primary
    },
    addStepButton: {
      background: colors.primary,
      hover: PRIMARY_HOVER_COLOR,
      backgroundLight: mainColor.lightenRelative(0.8).toHex(),
      backgroundDark: mainColor.lightenRelative(0.6).toHex(),
      iconColor: colors.primary,
      contrastingColor: colors.white
    },
    dnd: {
      dropzoneLineColor: colors.primary,
      dropzoneBgActiveColor: 'rgba(68, 147, 230, 0.15)',
      dropzoneBgWidth: '12px'
    },
    highlighted: `
      box-shadow: 0px 0px 0px 1px ${colors.search};
      border-radius: 5px;
    `,
    loading: colors.primary,
    editableLine: colors.primary,
    editableLineHighlighted: '#cb8843',
    copy: colors.primary,
    uploadFileBackground: colors.primary,
    uploadFileBackgroundFocused: colors.secondary,
    nlp: {
      model: {
        intent: {
          hover: {
            background: '#eff3f8'
          },
          button: {
            color: '#0a4fbd',
            border: '#c7d0d7',
            background: colors.silverLight
          }
        }
      }
    },
    dock: {
      title: '#7F8FA4',
      versions: {
        deployIcon: '#1991eb',
        latestIcon: '#36af47',
        unsavedIcon: '#f7981c'
      }
    }
  }
}

export type AppTheme = ReturnType<typeof defaultTheme>
export type ThemeCssProps = { theme: AppTheme }
export type AppColors = typeof colors

export const useAppTheme = () => useTheme() as AppTheme
export const useThemeColors = () => useAppTheme().colors
